// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";
import {
  useHandleDateChange,
} from "services/forms/hooks";
import { getActiveUser } from "services/api/users";

// 4. UI Components
import { DatePicker } from "ui/components/DatePicker";
import { Loader } from "ui/components/Loader";

// 5. Local types
import { VehicleOwnerFormProps } from "./types";
import { OwnerType } from "services/api/fleet/vehicles/owners/types";
import { getFleetVehicleOwnerTypeAPI } from "services/api/fleet/vehicles/owners/api";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { CardSelect } from "ui/components/CardSelect";



const VehicleOwnerForm: React.FC<VehicleOwnerFormProps> = (props) => {
  const { formValues, setFormValues, setVehicleInfo, errors = {} } = props;

  const { t } = useTranslate();
  const { user } = useSelector(getActiveUser);
  const companyName = user?.tenant?.companyName ?? null;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ownerTypes, setOwnerTypes] = useState<OwnerType[]>([]);

  useEffect(() => {
    fetchFleetVehicleOwnerType();
  }, []);

  // Postavi prvi tip vlasnika kao default kad god nema odabranog
  useEffect(() => {
    if (!ownerTypes.length) return;
    if (formValues.ownerTypeId) return;

    setFormValues((prev) => ({
      ...prev,
      ownerTypeId: ownerTypes[0].id,
    }));
  }, [ownerTypes, formValues.ownerTypeId]);

  const fetchFleetVehicleOwnerType = async () => {
    setIsLoading(true);

    let result: OwnerType[];

    try {
      result = await getFleetVehicleOwnerTypeAPI();
      setOwnerTypes(result);

      // Postavi prvi tip vlasnika kao default ako nije već odabran
      if (!formValues.ownerTypeId && result.length > 0) {
        setFormValues((prev) => ({
          ...prev,
          ownerTypeId: prev.ownerTypeId || result[0].id,
        }));
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  // Ažuriraj summary kad se promijene podaci o vlasniku
  useEffect(() => {
    const ownerType = ownerTypes.find(ot => ot.id === formValues.ownerTypeId);
    setVehicleInfo(prev => ({
      ...prev,
      ownerTypeId: formValues.ownerTypeId ?? null,
      ownerTypeName: ownerType?.name ?? null,
      ownerContactName: formValues.ownerTypeId === "C" ? companyName : null,
      ownerPurchaseDate: formValues.ownerTypeId !== "L" && formValues.purchaseDate
        ? new Date(formValues.purchaseDate).toLocaleDateString("hr-HR")
        : null,
      ownerLeaseActivationDate: formValues.ownerTypeId === "L" && formValues.leaseActivationDate
        ? new Date(formValues.leaseActivationDate).toLocaleDateString("hr-HR")
        : null,
    }));
  }, [formValues.ownerTypeId, formValues.purchaseDate, formValues.leaseActivationDate, ownerTypes]);

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  /** Custom handler za kontakt dropdown — ažurira i summary s imenom kontakta */
  const handleContactChange = useCallback((e: any, name: string, item: any) => {
    setFormValues((prev) => ({ ...prev, [name]: item.value }));
    setVehicleInfo((prev) => ({ ...prev, ownerContactName: item.text ?? null }));
  }, [setFormValues, setVehicleInfo]);

  const handleOwnerTypeSelect = useCallback((ownerTypeId: string) => {
    setFormValues((prev) => ({
      ...prev,
      ownerTypeId,
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex justify-center px-4 pt-12 h-[calc(100vh-150px)]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{t("admin.fleet.vehicle.edit.owner.title")}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">Odaberite vlasnika vozila kako biste nastavili s konfiguracijom. Dostupne su opcije vlasnik vozila, leasing kuća ili uslužna prodaja.</p>
          <form noValidate>
            <div className="flex flex-col">
              <div className="flex-1">
                <div className="px-4 pt-4 pb-1  rounded-sm p-4">
                  <div className="mb-6">
                    <CardSelect
                      options={ownerTypes.map((ot) => ({
                        id: ot.id,
                        icon: `/media/icons/${ot.icon}.svg`,
                        title: ot.name,
                        description: ot.description,
                      }))}
                      value={formValues.ownerTypeId}
                      onChange={handleOwnerTypeSelect}
                      columns={3}
                    />
                  </div>
                  {formValues.ownerTypeId && (
                    <div className="border border-slate-200 rounded-sm">
                      <div className="divide-y divide-slate-200">

                        {formValues.ownerTypeId !== "C" && (
                          <div className="p-4">
                            <div className="mb-2 flex justify-between">
                              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Kontakt vlasnika</h3>
                              <p className="text-xs text-slate-500">Odaberite kontakt osobe ili tvrtke koja je vlasnik vozila</p>
                            </div>
                            <ContactDropdown
                              placeholder={t(
                                "admin.fleet.vehicle.add.contact.owner.contact.placeholder"
                              )}
                              name="ownerContactId"
                              value={formValues.ownerContactId}
                              onChange={handleContactChange}
                              error={errors.ownerContactId}
                              required
                            />
                          </div>
                        )}
                        {formValues.ownerTypeId === "C" && companyName && (
                          <div className="p-4">
                            <div className="flex items-center gap-3 py-3 px-4 bg-blue-50 border border-blue-100 rounded-sm">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-blue-600 font-semibold text-sm">{companyName.charAt(0).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{companyName}</p>
                                <p className="text-xs text-slate-500">Vlasnik vozila je vaša tvrtka</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {formValues.ownerTypeId !== "L" && (
                          <div className="p-4">
                            <div className="mb-2 flex justify-between">
                              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Datum kupovine</h3>
                              <p className="text-xs text-slate-500">Unesite datum kada je vozilo kupljeno</p>
                            </div>
                            <DatePicker
                              name="purchaseDate"
                              placeholder={t(
                                "admin.fleet.vehicle.add.purchasedate.placeholder"
                              )}
                              onChange={handleDateChange}
                              error={errors.purchaseDate}
                              value={formValues.purchaseDate}
                              width="full"
                              required
                            />
                          </div>
                        )}
                        {formValues.ownerTypeId === "L" && (
                          <div className="p-4">
                            <div className="mb-2 flex justify-between">
                              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Datum aktivacije leasinga</h3>
                              <p className="text-xs text-slate-500">Unesite datum početka leasing ugovora</p>
                            </div>
                            <DatePicker
                              name="leaseActivationDate"
                              placeholder={t(
                                "admin.fleet.vehicle.add.leaseactivationdate.placeholder"
                              )}
                              onChange={handleDateChange}
                              error={errors.leaseActivationDate}
                              value={formValues.leaseActivationDate}
                              width="full"
                              required
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

    </>
  );
};
export default memo(VehicleOwnerForm);
